import { Autocomplete } from "@react-google-maps/api";
import { Form, Formik } from "formik";

import moment from "moment";
import { useRef, useState } from "react";
import { IoMdSearch } from "react-icons/io";
import { PiMapPinLineFill } from "react-icons/pi";
import * as yup from "yup";

import { ERROR_MSG, HOUR, MINUTES } from "../../constants/common";
import {
    RESET_LOADER,
    SET_ERROR,
    SET_LOADER,
} from "../../constants/contexConstant";
import {
    addressRegEx,
    nameRegEx,
    pinRegEx,
} from "../../constants/regularExpression";
import { NEW_ENQUIRY_ENDPOINT } from "../../constants/routes";
import { useContextState } from "../../context/ContextProvider";
import {
    debouncedEmailValidation,
    debouncedPhoneValidation,
    getCleanedAddressFromAutoComplete,
} from "../../helpers/helpers";
import cn from "../../lib/cn";
import { check } from "../../resources/js/images";
import request from "../../services/request";
import Container from "../Container";
import P from "../typography/P";
import Button from "../ui/Button";
import DrawerAndModalWrapper from "../ui/Drawer/DrawerAndModalWrapper";
import DateTimePickerBlockFormik from "../ui/Form/DateTimePickerBlockFormik";
import FormikLabeledInput from "../ui/Form/FormikLabeledInput";
import ReusableMap from "../ui/Map";
import Stepper from "../ui/Stepper";

const center = {
    lat: 18.5204303,
    lng: 73.8567437,
};
const EnquiryInternalUser = ({ isOpen, onClose }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const { dispatch } = useContextState();
    const [isLoaded, setIsLoaded] = useState(false);
    const [externalErrors, setExternalErrors] = useState({});

    const [markerPointA, setMarkerPointA] = useState(center);
    let autocompleteRef = useRef(null);
    const siteRef = useRef(null);

    const [autoFillAddress, setAutoFillAddress] = useState(
        "Pune, Maharashtra, India"
    );

    const [initialValues, setInitialValues] = useState({
        name: "",
        email: "",
        phoneNumber: "",
        billAmount: "",
        date: new Date(),
        hour: 10,
        minutes: 0,
        projectName: "",
        city: "",
        address: "",
        pincode: "",
        state: "",
        mapAddress: "",
        lat: center.lat,
        lng: center.lng,
    });

    const handlePlaceChanged = async (setFieldValue, setFieldTouched) => {
        let place = autocompleteRef.current.getPlace();

        const suggestion = siteRef.current.value;

        if (!place || !place.geometry) {
            return;
        }

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        const { city, state, cleanedAddress, pincode } =
            await getCleanedAddressFromAutoComplete(
                place,
                suggestion,
                lat,
                lng
            );

        const locationData = {
            lat,
            lng,
            address: cleanedAddress,
            city,
            state,
            pincode,
        };
        setAutoFillAddress(locationData);
        setMarkerPointA({ lat, lng });

        setFieldValue("address", cleanedAddress);
        setFieldValue("city", city);
        setFieldValue("state", state);
        setFieldValue("pincode", pincode);
        setFieldValue("lat", lat);
        setFieldValue("lng", lng);
    };
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 13);

    const getValidationSchema = () => {
        if (currentStep === 1) return enquiryNameEmailPhoneAndBillSchema;
        else if (currentStep === 2)
            return yup.object().shape({
                address: yup.string().required().label("Address"),
                projectName: yup
                    .string()
                    .required()
                    .min(3)
                    .max(50)
                    .matches(
                        nameRegEx,
                        "Name should start with letter and not contain any special characters or numbers"
                    )
                    .label("Project Name"),
            });
        else {
            return yup.object().shape({
                ...{
                    address: yup.string().required().label("Address"),
                    ...addressCityStatePinCodeSchemaShape,
                },
            });
        }
    };

    const handleStep = (values, actions) => {
        setInitialValues(values);
        actions.setSubmitting(false);
        if (currentStep === 1) {
            setCurrentStep(2);
        } else if (currentStep === 2) {
            setCurrentStep(3);
            actions.setSubmitting(false);
        } else {
            handleCompleteEnquiry(values, actions);
            actions.setSubmitting(true);
        }
    };

    const handleCompleteEnquiry = async (values, actions) => {
        try {
            let hoursInSecond = values.hour * 60 * 60;
            let minutes = values.minutes * 60;
            let reqData = {
                date: `${
                    moment(values.date).startOf("day").unix() +
                    hoursInSecond +
                    minutes
                }`,
                bill: values.billAmount,
                phone: `91${values.phoneNumber}`,
                name: values.name,
                email: values.email,
                city: values.city,
                state: values.state,
                pinCode: values.pincode,
                address: values.address,
                latitude: values.lat,
                longitude: values.lng,
                propertyName: values.projectName,
            };

            dispatch({ type: SET_LOADER });
            await request.authPost({
                endpoint: NEW_ENQUIRY_ENDPOINT,
                body: reqData,
            });

            actions.resetForm();
            dispatch({ type: RESET_LOADER });
            actions.setSubmitting(false);
            setCurrentStep(4);
        } catch (error) {
            dispatch({ type: RESET_LOADER });
            actions.setSubmitting(false);
            dispatch({
                type: SET_ERROR,
                payload: typeof error === "string" ? error : ERROR_MSG,
            });
        }
    };

    const handleCloseDrawerForm = (resetForm) => {
        setCurrentStep(1);
        resetForm();
        onClose();
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleStep}
            validationSchema={getValidationSchema()}
        >
            {({
                isValid,
                isSubmitting,
                dirty,
                errors,
                setFieldValue,
                setFieldTouched,
                resetForm,
                values,
            }) => (
                <Form>
                    <DrawerAndModalWrapper
                        isOpen={isOpen}
                        onClose={() => handleCloseDrawerForm(resetForm)}
                        drawerClassName="z-[99]"
                        drawerContentClassName="bg-white min-h-screen"
                        modalContentClass="bg-white"
                    >
                        <div className=" bg-white">
                            <div className="rounded-t-4 pb-7 lg:pb-0">
                                <Container>
                                    <div className="flex-1 flex flex-col items-center h-full">
                                        <div className="w-[80%] lg:w-5/6 mb-2 lg:mb-0">
                                            <Stepper
                                                steps={steps}
                                                currentStep={currentStep}
                                                onClick={(step) =>
                                                    step < currentStep &&
                                                    setCurrentStep(step)
                                                }
                                                theme={"light"}
                                                showCurrentStep={false}
                                            />
                                        </div>
                                    </div>
                                    {/* @DESC:: all are here @habib610 Fri June 06,2025 */}
                                    {currentStep < 4 ? (
                                        <div>
                                            <div
                                                className={cn(
                                                    "flex flex-col lg:grid lg:grid-cols-2 gap-x-2",
                                                    {
                                                        "hidden lg:hidden":
                                                            currentStep !== 1,
                                                    }
                                                )}
                                            >
                                                {/* @DESC:: step::1 @habib610 Fri June 06,2025 */}
                                                <FormikLabeledInput
                                                    label="Full Name"
                                                    placeholder="Enter Full Name"
                                                    required
                                                    name="name"
                                                />
                                                <FormikLabeledInput
                                                    label="Email Id"
                                                    placeholder="Enter Email Id"
                                                    name="email"
                                                    errorMessage={
                                                        externalErrors?.email
                                                    }
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        setFieldValue(
                                                            "email",
                                                            value
                                                        );
                                                        setExternalErrors(
                                                            (prev) => ({
                                                                ...prev,
                                                                email: "",
                                                            })
                                                        );
                                                        debouncedEmailValidation(
                                                            {
                                                                value,
                                                                setExternalErrors,
                                                            }
                                                        );
                                                    }}
                                                    required
                                                />

                                                <FormikLabeledInput
                                                    label="Phone Number"
                                                    placeholder="Enter Phone Number"
                                                    onChange={(e) => {
                                                        const val =
                                                            e.target.value;
                                                        setFieldValue(
                                                            "phoneNumber",
                                                            val
                                                        );
                                                        setExternalErrors(
                                                            (prev) => ({
                                                                ...prev,
                                                                phone: "",
                                                            })
                                                        );
                                                        debouncedPhoneValidation(
                                                            {
                                                                val,
                                                                setExternalErrors,
                                                            }
                                                        );
                                                    }}
                                                    errorMessage={
                                                        externalErrors.phoneNumber
                                                    }
                                                    name="phoneNumber"
                                                    required
                                                />
                                                <FormikLabeledInput
                                                    placeholder="Enter Bill Amount"
                                                    label="Bill Amount"
                                                    name="billAmount"
                                                    required
                                                />
                                            </div>
                                            <div
                                                className={cn(
                                                    "w-full flex flex-col",
                                                    {
                                                        hidden:
                                                            currentStep !== 1,
                                                    }
                                                )}
                                            >
                                                <DateTimePickerBlockFormik
                                                    label="Survey Date"
                                                    dateName="date"
                                                    hourName="hour"
                                                    minuteName="minutes"
                                                    maxDate={
                                                        new Date(
                                                            new Date().getFullYear(),
                                                            new Date().getMonth() +
                                                                1,
                                                            new Date().getDate()
                                                        )
                                                    }
                                                />
                                                <Button
                                                    disabled={
                                                        isSubmitting ||
                                                        !(isValid && dirty)
                                                    }
                                                    className="bg-primary w-full"
                                                >
                                                    Next
                                                </Button>
                                            </div>
                                            <div
                                                className={cn("flex flex-col", {
                                                    hidden: currentStep !== 2,
                                                })}
                                            >
                                                <FormikLabeledInput
                                                    placeholder="Enter Project Name"
                                                    label="Project Name"
                                                    required
                                                    name="projectName"
                                                />

                                                {isLoaded &&
                                                currentStep === 2 ? (
                                                    <Autocomplete
                                                        onPlaceChanged={() =>
                                                            handlePlaceChanged(
                                                                setFieldValue,
                                                                setFieldTouched
                                                            )
                                                        }
                                                        onLoad={(
                                                            autocomplete
                                                        ) => {
                                                            autocompleteRef.current =
                                                                autocomplete;
                                                        }}
                                                    >
                                                        <FormikLabeledInput
                                                            placeholder="Enter Your Location"
                                                            label="Search Address"
                                                            required
                                                            name="address"
                                                            id="siteLocationId"
                                                            shadow={false}
                                                            ref={siteRef}
                                                            icon={
                                                                <IoMdSearch />
                                                            }
                                                            iconWrapperClass="text-primary"
                                                        />
                                                    </Autocomplete>
                                                ) : (
                                                    <P>Loading...</P>
                                                )}

                                                <div className="grid grid-cols-1 gap-1.6 mt-1.6 lg:gap-x-[3rem] lg:mt-0">
                                                    <ReusableMap
                                                        center={markerPointA}
                                                        dimensions="w-[100%] h-[150px]"
                                                        address={
                                                            autoFillAddress
                                                        }
                                                        callback={() =>
                                                            setIsLoaded(true)
                                                        }
                                                    />
                                                </div>

                                                {values.address &&
                                                    values.projectName &&
                                                    isValid &&
                                                    dirty && (
                                                        <div className="mt-2">
                                                            <div className="flex items-center gap-1">
                                                                <PiMapPinLineFill
                                                                    size={26}
                                                                    className="text-orange"
                                                                />
                                                                <P className="text-sm text-orange font-vietnam">
                                                                    {
                                                                        values.projectName
                                                                    }
                                                                </P>
                                                            </div>

                                                            <P className="text-xs font-vietnam">
                                                                {values.address}
                                                            </P>
                                                        </div>
                                                    )}

                                                <Button
                                                    type="submit"
                                                    disabled={
                                                        isSubmitting ||
                                                        !(isValid && dirty)
                                                    }
                                                    className="bg-primary w-full mt-2"
                                                >
                                                    Confirm Address
                                                </Button>
                                            </div>

                                            <div
                                                className={cn("flex flex-col", {
                                                    hidden: currentStep !== 3,
                                                })}
                                            >
                                                <div className="grid grid-cols-1 gap-1.6 mt-1.6 lg:gap-x-[3rem] lg:mt-0 ">
                                                    <ReusableMap
                                                        center={markerPointA}
                                                        dimensions="w-[100%] h-[150px]"
                                                        address={
                                                            autoFillAddress
                                                        }
                                                        callback={() =>
                                                            setIsLoaded(true)
                                                        }
                                                    />
                                                </div>

                                                <div
                                                    className={cn(
                                                        "grid grid-cols-1 mt-2 "
                                                    )}
                                                >
                                                    <FormikLabeledInput
                                                        placeholder="Street Address"
                                                        label="Street Address"
                                                        required
                                                        name="address"
                                                    />
                                                    <FormikLabeledInput
                                                        placeholder="City"
                                                        label="City"
                                                        required
                                                        name="city"
                                                    />
                                                    <FormikLabeledInput
                                                        placeholder="State"
                                                        label="State"
                                                        required
                                                        name="state"
                                                    />
                                                    <FormikLabeledInput
                                                        placeholder="Pincode"
                                                        label="Pincode"
                                                        required
                                                        name="pincode"
                                                    />
                                                </div>
                                                <Button
                                                    type="submit"
                                                    disabled={
                                                        isSubmitting ||
                                                        !(isValid && dirty)
                                                    }
                                                    className="bg-primary w-full mt-2 lg:mt-0"
                                                >
                                                    Submit Details
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        /* @DESC:: confirmation sccreen @habib610 Fri June 06,2025 */
                                        <div>
                                            <div className="flex flex-col items-center justify-center ">
                                                <img
                                                    src={check}
                                                    alt="check mark"
                                                    className="w-6 h-6"
                                                />
                                                <P className="font-vietnam xl:text-base mt-2 text-center">
                                                    An email will be sent to
                                                    customer registered email id
                                                </P>
                                            </div>
                                            <Button
                                                className="bg-primary text-white mt-2 w-full "
                                                onClick={() => {
                                                    setCurrentStep(1);
                                                    onClose();
                                                }}
                                            >
                                                Done
                                            </Button>
                                        </div>
                                    )}
                                </Container>
                            </div>
                        </div>
                    </DrawerAndModalWrapper>
                </Form>
            )}
        </Formik>
    );
};

export default EnquiryInternalUser;

let steps = ["Consumer Profile", "Site Location", "Address Confirmation"];

const addressCityStatePinCodeSchemaShape = {
    address: yup
        .string()
        .required()
        .matches(
            addressRegEx,
            "Address should start with alphanumeric chars or letters and may contain (,-) "
        )
        .label("Address"),
    city: yup.string().required().max(100).label("City"),
    state: yup.string().required().max(80).label("State"),
    pincode: yup
        .string()
        .required()
        .matches(pinRegEx, "Pincode must be valid format")
        .label("Pincode"),
};

let nameEmailPhoneObject = {
    name: yup
        .string()
        .required()
        .min(3)
        .max(50)
        .matches(
            nameRegEx,
            "Name should start with letter and not contain any special characters or numbers"
        )
        .label("Full name"),
};
const enquiryNameEmailPhoneAndBillSchema = yup.object().shape({
    ...nameEmailPhoneObject,
    billAmount: yup
        .number()
        .typeError("Bill  must be valid number")
        .min(500, "Bill must be greater than or equal to 500")
        .max(10000000, "Bill must be less than or equal to 1Cr")
        .required()
        .label("Bill"),
    date: yup.date().required().label("Survey Date"),
    hour: yup
        .number()
        .required()
        .oneOf(HOUR.map((item) => item.value))
        .label("Hour"),
    minutes: yup
        .number()
        .required()
        .oneOf(MINUTES.map((item) => item.value))
        .label("Minutes"),
});
